import React, { useEffect, useRef, useState } from 'react';
import { Quote, User, Award, Users, Heart } from 'lucide-react';
import { siteData } from '../data/mock';

export const About = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-24 lg:py-32 bg-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 dot-pattern opacity-30" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {siteData.stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:shadow-lg transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-vibrant-blue to-vibrant-purple mb-2 font-outfit">
                {stat.value}
              </div>
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Founder Image */}
          <div className={`relative ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'} transition-all duration-700 delay-300`}>
            <div className="relative group">
              <div className="absolute inset-0 bg-vibrant-purple rounded-[3rem] rotate-3 opacity-20 group-hover:rotate-6 transition-transform duration-500" />
              <div className="absolute inset-0 bg-vibrant-blue rounded-[3rem] -rotate-3 opacity-20 group-hover:-rotate-6 transition-transform duration-500" />
              <img
                src={siteData.founder.image}
                alt="Hardik"
                className="relative w-full rounded-[3rem] shadow-2xl object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
              />

              <div className="absolute bottom-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-xl max-w-xs animate-float">
                <Quote className="w-8 h-8 text-vibrant-purple mb-2 opacity-50" />
                <p className="text-slate-800 font-medium italic">
                  "Technically precise, artistically inspired."
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'} transition-all duration-700 delay-300`}>
            <div className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-vibrant-green uppercase bg-green-50 rounded-full border border-green-100">
              The Creator
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-slate-900 mb-6 font-outfit">
              Meet the <span className="text-vibrant-blue">Developer</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Hi, I'm <span className="font-bold text-slate-900">Hardik</span>. I'm a passionate web developer with a love for creating beautiful, functional digital experiences.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              WedLabs was born from a desire to give couples a better way to invite their loved ones—one that matches the elegance of their special day while being eco-friendly and easily accessible.
            </p>

            <div className="flex gap-4">
              <a href={siteData.founder.social.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white hover:bg-vibrant-blue transition-colors">
                <User className="w-4 h-4" /> Portfolio
              </a>
              <a href="#contact" className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-200 hover:border-vibrant-blue hover:text-vibrant-blue transition-colors">
                Say Hello
              </a>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-32">
          <h3 className="text-3xl font-bold text-center mb-16 font-outfit relative">
            <span className="relative z-10">What Couples Say</span>
            <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-48 h-12 bg-vibrant-yellow/10 -skew-x-12 -z-0" />
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            {siteData.testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`bg-slate-50 p-8 rounded-[2rem] hover:shadow-xl transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150 + 500}ms` }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img src={t.image} alt={t.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-md" />
                  <div>
                    <div className="font-bold text-lg text-slate-900">{t.name}</div>
                    <div className="text-sm text-vibrant-blue">{t.role}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Heart key={star} className="w-4 h-4 fill-vibrant-red text-vibrant-red" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600 italic leading-relaxed">"{t.content}"</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
